import { createUuid } from '@core/utils';
import { captureException } from '@sentry/nextjs';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useShallow } from 'zustand/react/shallow';
import { useCheckWelloApp } from '@/hooks/useCheckWelloApp';
import { useDeviceInfoStore } from '@/stores/DeviceStore';
import type { MESSAGE_KEYWORD, MessageRequest, MessageResponse } from '../_utils/reserved-keywords';
import { postAppMessage } from '../_utils/sendAppMessage';
interface UseHandleAppProps {
  type: MESSAGE_KEYWORD;
}
export const useHandleApp = <TData = unknown,>({
  type
}: UseHandleAppProps) => {
  const {
    isWelloApp
  } = useCheckWelloApp();
  const [messageId, setMessageId] = useState('');
  const [appMessageLog, osType] = useDeviceInfoStore(useShallow(state => [state.appMessageLog, state.osType]));
  const [isPending, setIsPending] = useState(false);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const response = appMessageLog.get(messageId) as MessageResponse<TData> | undefined;
  useEffect(() => {
    if (response) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- zustand 외부 스토어 응답(response) 도착 시 isPending을 해제하는 의도적 패턴
      setIsPending(false);
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    }
  }, [response]);
  const request = useCallback((params?: MessageRequest['params']) => {
    try {
      if (!isWelloApp || !osType) return;
      setIsPending(true);
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      timeoutRef.current = setTimeout(() => {
        setIsPending(false);
        timeoutRef.current = null;
      }, 3000);
      const id = createUuid();
      setMessageId(id);
      const reqParams = JSON.stringify({
        type,
        id,
        params
      } satisfies MessageRequest);
      return postAppMessage(reqParams);
    } catch (error) {
      captureException(error);
      setIsPending(false);
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    }
  }, [isWelloApp, osType, type]);
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    };
  }, []);
  return {
    isPending,
    response,
    request
  };
};